import { X, Plus, Minus, ShoppingBag, Truck, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { trackInitiateCheckout, appendUTMsToUrl } from '../utils/metaPixel';
import { appendAclidToUrl } from '../utils/trafficJunky';

const FREE_DELIVERY_THRESHOLD = 30;

function CartProgress({ subtotal }) {
  const remaining = Math.max(0, FREE_DELIVERY_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_DELIVERY_THRESHOLD) * 100);

  return (
    <div style={{ padding: '14px 24px', backgroundColor: '#F7F7F5', borderBottom: '1px solid #F1F5F9' }}>
      <div className="flex items-center gap-2" style={{ marginBottom: '8px' }}>
        <Truck size={13} style={{ color: '#1B3D7E' }} strokeWidth={1.5} />
        <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.76rem', color: '#0D1A2E', fontWeight: 600 }}>
          {remaining > 0
            ? <>Spend <strong style={{ color: '#1B3D7E' }}>£{remaining.toFixed(2)}</strong> more for free UK delivery</>
            : <span style={{ color: '#1B3D7E' }}>You qualify for free UK delivery!</span>
          }
        </span>
      </div>
      <div style={{ height: '4px', backgroundColor: '#E2E8F0', borderRadius: '100px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${progress}%`, backgroundColor: '#1B3D7E', borderRadius: '100px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

function CartItem({ item, onSetQty, onRemove }) {
  return (
    <div data-testid="cart-item" style={{ display: 'flex', gap: '14px', paddingBottom: '20px', borderBottom: '1px solid #F1F5F9' }}>
      <div style={{ width: '72px', height: '72px', borderRadius: '12px', backgroundColor: '#F7F7F5', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {item.image && (
          <img src={item.image} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '0.88rem', color: '#0D1A2E', marginBottom: '2px', letterSpacing: '-0.01em' }}>{item.title}</p>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.72rem', color: '#94A3B8', marginBottom: '10px' }}>{item.subtitle}</p>

        <div className="flex items-center justify-between">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0', border: '1.5px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
            <button
              data-testid="cart-qty-minus"
              onClick={() => onSetQty(item.variantId, item.qty - 1)}
              style={{ width: '30px', height: '30px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', transition: 'background-color 0.15s' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F7F7F5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Minus size={12} strokeWidth={2.5} />
            </button>
            <span style={{ width: '32px', textAlign: 'center', fontFamily: 'Manrope, sans-serif', fontSize: '0.84rem', fontWeight: 700, color: '#0D1A2E' }}>{item.qty}</span>
            <button
              data-testid="cart-qty-plus"
              onClick={() => onSetQty(item.variantId, item.qty + 1)}
              style={{ width: '30px', height: '30px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', transition: 'background-color 0.15s' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F7F7F5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Plus size={12} strokeWidth={2.5} />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '0.95rem', color: '#1B3D7E' }}>
              £{(item.qty * item.price).toFixed(2)}
            </span>
            <button
              data-testid="cart-remove"
              onClick={() => onRemove(item.variantId)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CBD5E1', padding: '2px', transition: 'color 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#CBD5E1'}
            >
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CartDrawer() {
  const { open, setOpen, items, setQty, removeItem, subtotal, checkoutUrl, totalQty } = useCart();

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="cart-overlay"
        onClick={() => setOpen(false)}
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(13,26,46,0.45)', backdropFilter: 'blur(3px)',
          zIndex: 9990, opacity: open ? 1 : 0,
          pointerEvents: open ? 'all' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Drawer panel */}
      <div
        data-testid="cart-drawer"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: '420px',
          backgroundColor: '#FFFFFF', zIndex: 9991,
          display: 'flex', flexDirection: 'column',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: '-8px 0 40px rgba(13,26,46,0.12)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid #F1F5F9',
        }}>
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} style={{ color: '#1B3D7E' }} strokeWidth={1.5} />
            <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '1.05rem', color: '#0D1A2E', letterSpacing: '-0.01em' }}>
              Your Basket
            </span>
            {totalQty > 0 && (
              <span style={{
                backgroundColor: '#1B3D7E', color: '#FFFFFF',
                fontFamily: 'Manrope, sans-serif', fontSize: '0.7rem', fontWeight: 700,
                borderRadius: '100px', padding: '2px 8px',
              }}>
                {totalQty}
              </span>
            )}
          </div>
          <button
            data-testid="cart-close"
            onClick={() => setOpen(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '4px', display: 'flex', alignItems: 'center', borderRadius: '6px', transition: 'color 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#0D1A2E'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <CartProgress subtotal={subtotal} />

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <ShoppingBag size={40} style={{ color: '#E2E8F0', margin: '0 auto 16px' }} strokeWidth={1} />
              <p style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1.1rem', color: '#0D1A2E', marginBottom: '6px' }}>Your basket is empty</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.82rem', color: '#94A3B8' }}>Add RELOAD to start your journey.</p>
            </div>
          ) : (
            items.map((item) => (
              <CartItem key={item.variantId} item={item} onSetQty={setQty} onRemove={removeItem} />
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div style={{ padding: '20px 24px', borderTop: '1px solid #F1F5F9', backgroundColor: '#FFFFFF' }}>
            <div className="flex justify-between items-center" style={{ marginBottom: '6px' }}>
              <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.82rem', color: '#64748B', fontWeight: 600 }}>Subtotal</span>
              <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#0D1A2E' }}>£{subtotal.toFixed(2)}</span>
            </div>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.72rem', color: '#94A3B8', marginBottom: '16px' }}>
              {subtotal >= FREE_DELIVERY_THRESHOLD ? 'Free UK delivery included' : `+ £2.99 delivery (under £${FREE_DELIVERY_THRESHOLD})`}
            </p>

            <button
              data-testid="cart-checkout"
              onClick={() => {
                const url = appendAclidToUrl(appendUTMsToUrl(checkoutUrl));
                trackInitiateCheckout(totalQty, subtotal);
                // 350ms delay lets fbq's sendBeacon dispatch before the page unloads.
                // This is the standard pattern for tracking outbound link clicks.
                setTimeout(() => { window.location.href = url; }, 350);
              }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '100%', padding: '15px',
                backgroundColor: '#1B3D7E', color: '#FFFFFF',
                border: 'none', borderRadius: '12px',
                fontFamily: 'Manrope, sans-serif', fontSize: '0.88rem', fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                cursor: 'pointer', marginBottom: '10px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#152e61'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1B3D7E'}
            >
              Proceed to Checkout
            </button>

            <button
              onClick={() => setOpen(false)}
              style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '0.8rem', color: '#94A3B8', padding: '4px' }}
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
